/**
 * Routes Configuration
 * Centralized route paths for the application
 * 
 * Usage:
 * import { routes } from '@/config/routes';
 * router.push(routes.monitors.list);
 */

export const routes = {
  // Authentication
  auth: {
    login: '/login',
    forgotPassword: '/forgot-password',
    resetPassword: '/reset-password',
    completeRegistration: '/complete-registration',
  },

  // Main pages
  home: '/',
  
  // Monitors
  monitors: {
    list: '/monitors',
    detail: (id: string | number) => `/monitors/${id}`,
    maintenanceWindow: (id: string | number) => `/monitors/${id}/maintenance-window`,
    withTab: (tab: string) => `/monitors?tab=${tab}`,
    /** Synthetic monitor run / results page (no API yet; integrate later) */
    syntheticRun: (id: string | number) => `/monitors/synthetic/${id}`,
  },

  // Configure
  configure: {
    main: '/configure',
  },

  // Settings
  settings: {
    main: '/settings',
  },

  // Alerts
  alerts: {
    list: '/alerts',
    detail: (id: string | number) => `/alerts/${id}`,
  },

  // Triage (formerly Incidents)
  triage: {
    list: '/triage',
    detail: (id: string | number) => `/triage/${id}`,
    alertDetail: (id: string | number) => `/triage/alerts/${id}`,
  },

  // Log Analyzer
  logAnalyzer: {
    main: '/log-analyzer',
  },

  // Sales Analyzer
  salesAnalyzer: {
    main: '/sales-analyzer',
  },

  // Page Scraper (under monitors for sidebar activation)
  pageScrapper: {
    main: '/monitors?tab=scrapper',
    withTab: (tab: string) => `/monitors/scrapper?tab=${tab}`,
    detail: (id: string | number) => `/monitors/scrapper/${id}`,
  },

  // Dashboard (hidden but accessible)
  dashboard: {
    main: '/',
  },

  // Log Browser (hidden but accessible)
  logBrowser: {
    main: '/log-browser',
  },
};

/**
 * API Routes
 */
export const apiRoutes = {
  // Authentication
  auth: {
    login: '/api/v2/auth/login',
    validateOtp: '/api/v2/otp/validate_otp',
    validateToken: '/api/v2/auth/validate_token',
    logout: '/api/v2/auth/logout',
    forgotPassword: '/api/v2/auth/forgot_password',
    resetPassword: '/api/v2/auth/reset_password_with_token',
    resetCurrentPassword: '/api/v2/auth/reset_current_password',
    completeRegistration: '/api/v2/auth/complete_registration',
  },

  // Monitors/Sites
  sites: {
    list: '/api/sites',
    detail: (id: string | number) => `/api/sites/${id}`,
    create: '/api/sites',
    update: (id: string | number) => `/api/sites/${id}`,
    delete: (id: string | number) => `/api/sites/${id}`,
  },

  // Triage (formerly Incidents)
  triage: {
    list: '/api/incidents',
    detail: (id: string | number) => `/api/incidents/${id}`,
  },

  // Synthetic Monitors (CRUD – core API; list is on Scraper API at syntheticMonitorTest.monitorsList)
  syntheticMonitors: {
    list: '/api/synthetic-monitors',
    detail: (id: string | number) => `/api/synthetic-monitors/${id}`,
    create: '/api/synthetic-monitors',
    update: (id: string | number) => `/api/synthetic-monitors/${id}`,
    delete: (id: string | number) => `/api/synthetic-monitors/${id}`,
  },

  // Synthetic Monitor Test Execution + List + Detail + Create (GlassX Scraper API – use with scrapperApi; base = NEXT_PUBLIC_SCRAPPER_API_BASE_URL)
  // List: GET /synthetic_monitor/monitors?search=&scenario_group=&sort=created_desc
  // Detail: GET /synthetic_monitor/monitors/:id
  // Create: POST /synthetic_monitor/monitors
  // Update: PUT /synthetic_monitor/monitors/:id
  // Delete: DELETE /synthetic_monitor/monitors/:id
  syntheticMonitorTest: {
    monitorsList: '/synthetic_monitor/monitors',
    monitorDetail: (id: string | number) => `/synthetic_monitor/monitors/${id}`,
    monitorCreate: '/synthetic_monitor/monitors',
    monitorCreateFlow: '/synthetic_monitor/monitors/create_flow',
    monitorUpdate: (id: string | number) => `/synthetic_monitor/monitors/${id}`,
    monitorDelete: (id: string | number) => `/synthetic_monitor/monitors/${id}`,
    health: '/synthetic_monitor/health',
    testLoginSuccessful: '/synthetic_monitor/test_login_successful',
    testLoginInvalidCredentials: '/synthetic_monitor/test_login_invalid_credentials',
    testLoginNonexistentEmail: '/synthetic_monitor/test_login_nonexistent_email',
    testLoginEmptyFields: '/synthetic_monitor/test_login_empty_fields',
    testPlp: '/synthetic_monitor/test_plp',
    testPlpPageLoad: '/synthetic_monitor/test_plp_page_load',
    testPlpPageGrid: '/synthetic_monitor/test_plp_page_grid',
    testPlpProductAvailability: '/synthetic_monitor/test_plp_product_availability',
    testPlpAddToCart: '/synthetic_monitor/test_plp_add_to_cart',
    testPdpBasicContent: '/synthetic_monitor/test_pdp_basic_content',
    testPdpProductDetails: '/synthetic_monitor/test_pdp_product_details',
    testPdpImageGallery: '/synthetic_monitor/test_pdp_image_gallery',
    testPdpAddToCart: '/synthetic_monitor/test_pdp_add_to_cart',
    testCartViewProducts: '/synthetic_monitor/test_cart_view_products',
    testCartUpdateQuantity: '/synthetic_monitor/test_cart_update_quantity',
    testCartRemoveProduct: '/synthetic_monitor/test_cart_remove_product',
    testCartProceedCheckout: '/synthetic_monitor/test_cart_proceed_checkout',
    testCheckoutAccess: '/synthetic_monitor/test_checkout_access',
    testCheckoutShippingAddress: '/synthetic_monitor/test_checkout_shipping_address',
    testCheckoutOrderSummary: '/synthetic_monitor/test_checkout_order_summary',
    testCheckoutPaymentMethod: '/synthetic_monitor/test_checkout_payment_method',
    testCheckoutPlaceOrder: '/synthetic_monitor/test_checkout_place_order',
    testCheckoutOrderConfirmation: '/synthetic_monitor/test_checkout_order_confirmation',
  },

  // Scrapper
  scrapper: {
    list: '/api/scrapper/configs',
    detail: (id: string | number) => `/api/scrapper/configs/${id}`,
    create: '/api/scrapper/configs',
    update: (id: string | number) => `/api/scrapper/configs/${id}`,
    delete: (id: string | number) => `/api/scrapper/configs/${id}`,
    start: (id: string | number) => `/api/scrapper/configs/${id}/start`,
    stop: (id: string | number) => `/api/scrapper/configs/${id}/stop`,
    metrics: (url: string) => `/api/scrapper/metrics?url=${encodeURIComponent(url)}`,
    errors: (url: string) => `/api/scrapper/errors?url=${encodeURIComponent(url)}`,
  },

  // Notifications
  notifications: {
    list: '/notifications',
    stats: '/notifications/stats',
    markAsRead: (id: string | number) => `/notifications/${id}/read`,
    markAsUnread: (id: string | number) => `/notifications/${id}/unread`,
    readAll: '/notifications/read-all',
    unreadAll: '/notifications/unread-all',
    delete: (id: string | number) => `/notifications/${id}`,
    deleteAll: '/notifications',
  },

  // Admin/User Management
  admin: {
    createUser: '/api/v2/admin/create_user',
    listUsers: '/api/v2/admin/users',
    updateUser: '/api/v2/admin/user',
    deleteUser: '/api/v2/admin/user/delete',
    inviteAction: '/api/v2/admin/registration/invite_action',
    toggleUserStatus: '/api/v2/admin/user/toggle_status',
  },

  // Logos
  logos: {
    upload: '/api/v2/logos/',
    list: '/api/v2/logos/',
    getFile: (id: string) => `/api/v2/logos/${id}`,
    getMetadata: (id: string) => `/api/v2/logos/${id}/info`,
    update: (id: string) => `/api/v2/logos/${id}`,
    delete: (id: string) => `/api/v2/logos/${id}`,
    getByOrganization: (organizationId: string) => `/api/v2/logos/organization/${organizationId}`,
    getByTenant: (tenantId: string) => `/api/v2/logos/tenant/${tenantId}`,
  },

  // Organizations
  organizations: {
    getById: (organizationId: string) => `/api/v2/organizations/${organizationId}`,
    update: (organizationId: string) => `/api/v2/organizations/${organizationId}`,
  },
};

/**
 * Public paths that don't require authentication
 */
export const publicPaths = [
  routes.auth.login,
  routes.auth.forgotPassword,
  routes.auth.resetPassword,
  routes.auth.completeRegistration,
];

/**
 * Check if a path is public (doesn't require authentication)
 */
export function isPublicPath(path: string): boolean {
  return publicPaths.includes(path);
}

/**
 * Check if a path is a static asset
 */
export function isStaticAsset(path: string): boolean {
  const staticExtensions = [
    '.svg',
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.webp',
    '.ico',
    '.css',
    '.js',
    '.json',
    '.woff',
    '.woff2',
    '.ttf',
    '.eot',
  ];
  
  return staticExtensions.some(ext => path.toLowerCase().endsWith(ext));
}

/**
 * Check if a path is an API route
 */
export function isApiRoute(path: string): boolean {
  return path.startsWith('/api/');
}

/**
 * Check if a path is a Next.js internal route
 */
export function isNextInternalRoute(path: string): boolean {
  return path.startsWith('/_next/');
}

